import initSentry from 'lib/initSentry';
initSentry();
import { withUrqlClient, NextUrqlAppContext } from 'next-urql';
import { withUrqlSsr } from 'lib/urql';
import { Provider } from 'react-redux';
import { useClientStore, createServerStore } from '../lib/redux';
import BaseLayout from '../app/components/App/BaseLayout';
import BaseHtmlHeader from '../app/components/App/BaseHtmlHeader';
import NextApp, { AppProps, NextWebVitalsMetric } from 'next/app';
import '../app/components/App/normalize.css';
import setupAbiskoServer from 'lib/abiskoServer';
import setupAbiskoClient from '../client/abisko';
import React, { useEffect, useState } from 'react';
import { fromJS } from 'immutable';
import { loadPersistentActions } from 'app/utils/persistentState';
import { Debug } from '@svt/debug';
import { Store } from 'redux';
import { fetchParentalLock } from 'app/actions/parentalLock';
import { trackNonInteractiveEvent } from 'app/utils/statistics/clientStatistics';
import { useRouter } from 'next/router';
import { SlashTiming } from 'app/ui-components/design-tokens';
import VideoModal from 'app/components/VideoModal/VideoModal';

const { error: errorLogger } = Debug('play:client:app:error');
interface Props extends AppProps {
    initialStore: object;
    reduxStore: Store<any, any>;
}

const DelayedUnmount = ({ shouldDelay, children }) => {
    const [displayChildren, setDisplayChildren] = useState(children);

    useEffect(() => {
        let timeout;

        if (shouldDelay) {
            timeout = setTimeout(() => {
                setDisplayChildren(children);
            }, SlashTiming * 1.1);
        } else {
            setDisplayChildren(children);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [shouldDelay, children]);

    return displayChildren;
};

const App = ({ Component, pageProps, initialStore, reduxStore }: Props) => {
    const store = useClientStore(fromJS(initialStore), reduxStore);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            loadPersistentActions(store.dispatch);
            setupAbiskoClient(store);
            store.dispatch(fetchParentalLock());
        }
    }, []); // eslint-disable-line

    const router = useRouter();
    const videoModalOpen = Boolean(router.query.modalId);

    const RenderComponent = videoModalOpen ? React.Fragment : Component;

    return (
        <>
            <BaseHtmlHeader />
            <Provider store={store}>
                <BaseLayout>
                    <DelayedUnmount shouldDelay={router.query.delayUnmount}>
                        <RenderComponent {...pageProps} />
                    </DelayedUnmount>
                    {videoModalOpen && <VideoModal id={router.query.modalId} />}
                </BaseLayout>
            </Provider>
        </>
    );
};

App.getInitialProps = async (ctx: NextUrqlAppContext) => {
    const store = createServerStore(undefined);
    const appProps = await NextApp.getInitialProps(ctx);

    const { req } = ctx.ctx;
    if (req) {
        try {
            await setupAbiskoServer(store, req);
        } catch (e) {
            errorLogger('error abisko', e);
        }

        req.reduxStore = store;
    }

    return {
        ...appProps,
        initialStore: store.getState().toJS(),
        reduxStore: store,
    };
};

export function reportWebVitals(metric: NextWebVitalsMetric) {
    trackNonInteractiveEvent({
        name: 'performance',
        eventValues: {
            performanceId: metric.id,
            performanceName: metric.name,
            performanceLabel: metric.label,
            performanceStartTime: metric.startTime,
            performanceValue: metric.value,
        },
    });
}

export default withUrqlClient(withUrqlSsr)(App);
